/**
 * Enti — sezione riservata al Super Admin.
 *
 * Il sistema nasce multi-ente: l'`Ente` e' la radice del multi-tenant e ogni
 * query applicativa filtra per `ente_id`. Un secondo Comune si attiva per
 * configurazione, non modificando il codice.
 *
 * Due dati sono di conformita' e non di anagrafica, e per questo la pagina li
 * verifica invece di limitarsi a mostrarli: il **dominio** deve essere nella
 * forma `comune.[nome].[sigla].it` e rispondere senza `www` (C.SI.5.2), e il
 * **codice fiscale** dell'ente e' di 11 cifre numeriche secondo il formato dei
 * campi elementari ANAC.
 */

import { useCallback, useEffect } from "react";
import { elencaEnti } from "../api/client";
import type { Ente } from "../api/types";
import { Avviso, Chip, Scheda, Scheletro, StatoVuoto } from "../components/primitive";
import { validaCodiceFiscaleEnte } from "../lib/formats";
import { useRisorsa } from "../lib/useRisorsa";
import { IntestazionePagina } from "../layout/Guscio";

/** Verifica la forma del dominio istituzionale prevista da C.SI.5.2. */
function verificaDominio(dominio: string): { conforme: boolean; motivo: string } {
  if (!dominio) return { conforme: false, motivo: "Dominio non indicato." };
  if (dominio.startsWith("www.")) {
    return {
      conforme: false,
      motivo: "Il dominio deve rispondere senza il prefisso «www».",
    };
  }
  if (!/^comune\.[a-z0-9-]+\.[a-z]{2}\.it$/i.test(dominio)) {
    return {
      conforme: false,
      motivo: "Forma attesa: comune.[nome].[sigla provincia].it",
    };
  }
  return { conforme: true, motivo: "Conforme alla forma prevista." };
}

export function Enti() {
  const carica = useCallback((segnale: AbortSignal) => elencaEnti(segnale), []);
  const { dato, inCaricamento, errore } = useRisorsa(carica, []);

  useEffect(() => {
    document.title = "Enti · Amministrazione trasparente";
  }, []);

  const enti = dato ?? [];

  return (
    <div className="pila">
      <IntestazionePagina
        sopratitolo="Amministrazione"
        titolo="Enti"
        sottotitolo="Comuni configurati sulla piattaforma. Ogni ente ha la propria alberatura, i propri utenti e il proprio dominio istituzionale."
      />

      {errore ? (
        <Avviso tono="critico" titolo="Elenco non disponibile">
          {errore.messaggioEsteso}
        </Avviso>
      ) : null}

      <Avviso tono="primario" titolo="Perche' questi controlli">
        Il dominio e il codice fiscale non sono soltanto anagrafica: il primo e' oggetto del
        criterio C.SI.5.2, il secondo segue il formato dei campi elementari ANAC. Una difformita'
        qui si propaga a tutti gli URL pubblicati e alla at_map.
      </Avviso>

      <Scheda titolo={`Enti configurati (${enti.length})`} fitto>
        {inCaricamento ? (
          <div style={{ padding: "1.25rem" }}>
            <Scheletro righe={4} />
          </div>
        ) : enti.length === 0 ? (
          <StatoVuoto
            titolo="Nessun ente configurato"
            testo="L'attivazione di un nuovo Comune avviene per configurazione, senza modifiche al codice."
          />
        ) : (
          <div className="tabella-guscio">
            <table className="tabella">
              <thead>
                <tr>
                  <th scope="col">Denominazione</th>
                  <th scope="col">Dominio</th>
                  <th scope="col">Codice fiscale</th>
                  <th scope="col">Ambito ANAC</th>
                  <th scope="col">Verifiche</th>
                </tr>
              </thead>
              <tbody>
                {enti.map((ente) => (
                  <RigaEnte key={ente.id} ente={ente} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Scheda>
    </div>
  );
}

function RigaEnte({ ente }: { ente: Ente }) {
  const dominio = verificaDominio(ente.dominio ?? "");
  const erroreCodice = ente.codice_fiscale ? validaCodiceFiscaleEnte(ente.codice_fiscale) : null;

  return (
    <tr>
      <td className="tabella__principale">{ente.denominazione}</td>
      <td className="dato">{ente.dominio || "—"}</td>
      <td className="dato">{ente.codice_fiscale || "—"}</td>
      <td>
        <Chip tono={ente.ambito_anac === "c1" ? "positivo" : "attenzione"} pallino={false}>
          {ente.ambito_anac ?? "non indicato"}
        </Chip>
      </td>
      <td>
        <div className="pila-stretta">
          <Chip tono={dominio.conforme ? "positivo" : "critico"}>
            {dominio.conforme ? "Dominio conforme" : "Dominio da verificare"}
          </Chip>
          {!dominio.conforme ? <span className="campo__aiuto">{dominio.motivo}</span> : null}
          {erroreCodice ? <span className="campo__errore">{erroreCodice}</span> : null}
        </div>
      </td>
    </tr>
  );
}
